import { useEffect } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";
import { cn } from "@/lib/utils/cn";

// ── Store ─────────────────────────────────────────────────────────────────────

type ToastKind = "success" | "error" | "info";

interface ToastItem {
  id: number;
  message: string;
  kind: ToastKind;
}

interface ToastStore {
  items: ToastItem[];
  add: (message: string, kind: ToastKind) => void;
  remove: (id: number) => void;
}

let _nextId = 1;

const useToastStore = create<ToastStore>((set) => ({
  items: [],
  add: (message, kind) => {
    const id = _nextId++;
    set((s) => ({ items: [...s.items, { id, message, kind }] }));
    setTimeout(() => {
      set((s) => ({ items: s.items.filter((t) => t.id !== id) }));
    }, 4000);
  },
  remove: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

// ── Public API ────────────────────────────────────────────────────────────────

export const toast = {
  success: (message: string) => useToastStore.getState().add(message, "success"),
  error: (message: string) => useToastStore.getState().add(message, "error"),
  info: (message: string) => useToastStore.getState().add(message, "info"),
};

// ── Renderer ──────────────────────────────────────────────────────────────────

const KIND_CLASSES: Record<ToastKind, string> = {
  success: "bg-green-600 text-white",
  error: "bg-red-600 text-white",
  info: "bg-blue-600 text-white",
};

function ToastItem({ item }: { item: ToastItem }) {
  const remove = useToastStore((s) => s.remove);
  useEffect(() => {
    return () => {}; // cleanup handled by store timeout
  }, []);

  return (
    <div
      role="alert"
      onClick={() => remove(item.id)}
      className={cn(
        "flex cursor-pointer items-start gap-2 rounded-md px-4 py-3 shadow-lg text-sm max-w-sm",
        KIND_CLASSES[item.kind],
      )}
    >
      <span className="flex-1">{item.message}</span>
    </div>
  );
}

/**
 * ToastPortal — mount once at the app root to render toasts.
 * Usage: <ToastPortal /> inside your root component.
 */
export function ToastPortal() {
  const items = useToastStore((s) => s.items);

  return createPortal(
    <div className="fixed right-4 top-4 z-50 flex flex-col gap-2">
      {items.map((item) => (
        <ToastItem key={item.id} item={item} />
      ))}
    </div>,
    document.body,
  );
}
